// Per-kind element renderers — the O(n), template-specific block renderers.
//
// This module IS the pluggable seam (project_editor_is_material_surface:
// per-template block renderers, O(n); the editor shell is O(1) and generic).
// MaterialDocument owns no element kind; each kind's body is drawn by an entry
// in this registry. A second template ships its OWN registry and reuses the
// shell unchanged — extension is adding a module here, not editing a switch in
// the shell.
//
// Each renderer receives its exact element variant (no cast — MaterialDocument's
// total dispatch narrows before calling) plus the pending proposal. A renderer
// only draws a FIELD-grain diff (scalar-edit / list-edit) inside its content,
// via InlineDiff; BLOCK-grain ops (add / remove / move) are the shell's job, so
// the renderer just draws its normal body for those. This split keeps the
// renderer ignorant of structural change — it renders content, the shell renders
// structure.
//
// Pure: props in, elements out. No state, no I/O.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Badge } from "../Badge/Badge.js";
import { Button } from "../Button/Button.js";
import { Heading } from "../Heading/Heading.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Prose } from "../Prose/Prose.js";
import { SegmentedControl } from "../SegmentedControl/SegmentedControl.js";
import { Text } from "../Text/Text.js";
import { EditableText } from "./EditableText/EditableText.js";
import { InlineDiff } from "./InlineDiff/InlineDiff.js";
import { applyRule, MARKDOWN_RULES } from "./inputRules.js";
import "./renderers.css";
import type {
    DiffOp,
    ElementId,
    ElementRendererProps,
    ElementRenderers,
    ElementRunProps,
    HeadingElement,
    McqElement,
    ObjectiveElement,
    ProseElement,
} from "./types.js";

// A heading level parsed back from the SegmentedControl's string value. Total:
// the control only ever emits one of its three option values, and an unexpected
// value falls back to level 2 (the neutral default) rather than throwing.
const toLevel = (value: string): 1 | 2 | 3 =>
    value === "1" ? 1 : value === "3" ? 3 : 2;

// A field-grain edit the renderer should draw in place of the primary text.
// Block-grain ops return undefined (the shell wraps the body for those).
const fieldEditOf = (proposal?: {
    readonly op: DiffOp;
}): DiffOp | undefined => {
    if (!proposal) return undefined;
    const { op } = proposal;
    return op.type === "scalar-edit" ||
        op.type === "list-edit" ||
        op.type === "inline-edit"
        ? op
        : undefined;
};

const HeadingRenderer = ({
    element,
    proposal,
    onEditText,
    onSetHeadingLevel,
    onEnterAtEnd,
    onBackspaceEmpty,
    autoEdit,
    onAutoEditConsumed,
}: ElementRendererProps<HeadingElement>): ReactNode => {
    const { t } = useLingui();
    const edit = fieldEditOf(proposal);
    return (
        <div className="bilda-render-heading">
            <Heading level={element.level} size="xl">
                {edit ? (
                    <InlineDiff op={edit} />
                ) : onEditText ? (
                    <EditableText
                        value={element.text}
                        ariaLabel={t`Edit heading`}
                        onCommit={(text) => onEditText(element.id, text)}
                        onEnterAtEnd={onEnterAtEnd}
                        onBackspaceEmpty={onBackspaceEmpty}
                        autoEdit={autoEdit}
                        onAutoEditConsumed={onAutoEditConsumed}
                    />
                ) : (
                    element.text
                )}
            </Heading>
            {onSetHeadingLevel ? (
                // Enumerated attr, focus-revealed (progressive disclosure): the
                // level has no natural locus in the reading face, so a segmented
                // H1/H2/H3 appears when the heading is focused/hovered.
                <div className="bilda-render-heading__level">
                    <SegmentedControl
                        size="sm"
                        ariaLabel={t`Heading level`}
                        value={String(element.level)}
                        options={[
                            { value: "1", label: t`H1` },
                            { value: "2", label: t`H2` },
                            { value: "3", label: t`H3` },
                        ]}
                        onChange={(v) =>
                            onSetHeadingLevel(element.id, toLevel(v))
                        }
                    />
                </div>
            ) : null}
        </div>
    );
};

const ProseRenderer = ({
    element,
    proposal,
    onEditText,
    onInputRule,
    slashTriggerState,
    onSlashTrigger,
    onSlashNavigate,
    onSlashCommitActive,
    onEnterAtEnd,
    onBackspaceEmpty,
    autoEdit,
    onAutoEditConsumed,
}: ElementRendererProps<ProseElement>): ReactNode => {
    const { t } = useLingui();
    const edit = fieldEditOf(proposal);
    // A prose edit renders the before/after inline. Otherwise: an author-editable
    // material wires an EditableText over the raw markdown SOURCE, whose resting
    // face keeps the parsed Prose reading (renderDisplay) — you read the rendered
    // block, you edit the markdown. A read-only material just parses the markdown.
    if (edit) return <InlineDiff op={edit} />;
    return onEditText ? (
        <EditableText
            value={element.markdown}
            ariaLabel={t`Edit paragraph`}
            onCommit={(markdown) => onEditText(element.id, markdown)}
            renderDisplay={(markdown) => <Prose>{markdown}</Prose>}
            // Live markdown-rule + slash-trigger detection (only wired when the
            // host offers the callbacks — same progressive-disclosure discipline
            // as every other affordance here). `applyRule` needs THIS element to
            // build the destination (RuleContext) — that is why matching happens
            // in EditableText (a pure string→match lookup, no Element needed) but
            // the EFFECT is built here, one level up, where the ProseElement is
            // in scope.
            rules={onInputRule ? MARKDOWN_RULES : undefined}
            onRuleMatch={
                onInputRule
                    ? (match) =>
                          onInputRule(applyRule({ prose: element }, match))
                    : undefined
            }
            slashTriggerState={slashTriggerState}
            onSlashTrigger={onSlashTrigger}
            onSlashNavigate={onSlashNavigate}
            onSlashCommitActive={onSlashCommitActive}
            onEnterAtEnd={onEnterAtEnd}
            onBackspaceEmpty={onBackspaceEmpty}
            autoEdit={autoEdit}
            onAutoEditConsumed={onAutoEditConsumed}
        />
    ) : (
        <Prose>{element.markdown}</Prose>
    );
};

const ObjectiveRenderer = ({
    element,
    proposal,
    onEditText,
    onEnterAtEnd,
    onBackspaceEmpty,
    autoEdit,
    onAutoEditConsumed,
}: ElementRendererProps<ObjectiveElement>): ReactNode => {
    const { t } = useLingui();
    const edit = fieldEditOf(proposal);
    // One tight line of the run's list (ObjectivesRun names it once).
    return (
        <div className="bilda-render-objective">
            <Text as="div" className="bilda-render-objective__body">
                {edit ? (
                    <InlineDiff op={edit} />
                ) : onEditText ? (
                    <EditableText
                        value={element.text}
                        ariaLabel={t`Edit objective`}
                        onCommit={(text) => onEditText(element.id, text)}
                        onEnterAtEnd={onEnterAtEnd}
                        onBackspaceEmpty={onBackspaceEmpty}
                        autoEdit={autoEdit}
                        onAutoEditConsumed={onAutoEditConsumed}
                    />
                ) : (
                    element.text
                )}
            </Text>
        </div>
    );
};

// Above this many objectives the list folds behind its heading.
const OBJECTIVES_FOLD_AT = 5;

// A run of consecutive objectives as ONE compact "Learning objectives" list:
// a small heading, then the objectives as tight lines. A long list can be
// folded (open by default, so nothing is hidden on arrival).
const ObjectivesRun = ({ count, children }: ElementRunProps): ReactNode => {
    const { t } = useLingui();
    const heading = (
        <Text as="span" size="sm" weight="semibold" tone="dim">
            {t`Learning objectives`}
        </Text>
    );
    return count >= OBJECTIVES_FOLD_AT ? (
        <details className="bilda-render-objectives" open>
            <summary className="bilda-render-objectives__head">
                {heading}
            </summary>
            {children}
        </details>
    ) : (
        <section
            className="bilda-render-objectives"
            aria-label={t`Learning objectives`}
        >
            <div className="bilda-render-objectives__head">{heading}</div>
            {children}
        </section>
    );
};

const correctGlyph = <Icon name="check" size="xs" />;

// The composite kind — proves the seam is real: an MCQ card with a stem, an
// A/B/C/D choice list, and a correct-answer marker (colour never alone: the
// "Correct" Badge carries icon + label). A stem edit renders inline.
const McqRenderer = ({
    element,
    proposal,
    onEditText,
    onEditFeedback,
    onMarkChoiceCorrect,
    onAddChoice,
    onRemoveChoice,
    onMoveChoice,
    onAllowSingleAnswer,
    onAllowMultipleAnswers,
}: ElementRendererProps<McqElement>): ReactNode => {
    const { t } = useLingui();
    const edit = fieldEditOf(proposal);
    const letters = ["A", "B", "C", "D", "E", "F"];
    // Interactive correctness lives ONLY on single-answer MCQs for now (the
    // multiple-answer checkbox is deferred). A radio group named by the MCQ id:
    // selecting a choice marks it correct, and the edit fold clears the others —
    // so "exactly one key" is a structural consequence of the radio group, not a
    // rule re-checked here. The "Correct" badge stays as the a11y-safe status
    // (colour never carries meaning alone).
    const editableCorrect =
        onMarkChoiceCorrect !== undefined && element.answerMode === "single";
    // A choice may be removed only when more than one remains — an MCQ with a
    // single choice is degenerate, so the last remove is not offered.
    const removable =
        onRemoveChoice !== undefined && element.choices.length > 1;
    const mcqId: ElementId = element.id;
    return (
        <div className="bilda-render-mcq">
            <div className="bilda-render-mcq__head">
                <Text as="p" size="lg" weight="medium">
                    {edit ? <InlineDiff op={edit} /> : element.stem}
                </Text>
                {onAllowSingleAnswer !== undefined &&
                onAllowMultipleAnswers !== undefined ? (
                    // Enumerated attr, focus-revealed. Picking a mode dispatches
                    // its OWN command (never a setAnswerMode flag): "single" →
                    // allow-single-answer (narrows the key), "multiple" →
                    // allow-multiple-answers (widens it).
                    <div className="bilda-render-mcq__mode">
                        <SegmentedControl
                            size="sm"
                            ariaLabel={t`Answer mode`}
                            value={element.answerMode}
                            options={[
                                { value: "single", label: t`Single answer` },
                                {
                                    value: "multiple",
                                    label: t`Multiple answers`,
                                },
                            ]}
                            onChange={(v) =>
                                v === "multiple"
                                    ? onAllowMultipleAnswers(mcqId)
                                    : onAllowSingleAnswer(mcqId)
                            }
                        />
                    </div>
                ) : null}
            </div>
            <ol className="bilda-render-mcq__choices">
                {element.choices.map((choice, i) => (
                    <li key={choice.id} className="bilda-render-mcq__choice">
                        {editableCorrect ? (
                            <input
                                type="radio"
                                className="bilda-render-mcq__correct-radio"
                                name={`mcq-correct-${element.id}`}
                                checked={choice.correct}
                                aria-label={t`Mark as the correct answer`}
                                onChange={() =>
                                    onMarkChoiceCorrect?.(choice.id)
                                }
                            />
                        ) : null}
                        <Text as="span" tone="dim" size="sm">
                            {letters[i] ?? "?"}.
                        </Text>
                        <div className="bilda-render-mcq__choice-body">
                            {/* The answer, with its "Correct" mark right
                                beside it (not adrift at the row's end). */}
                            <div className="bilda-render-mcq__choice-text">
                                {onEditText ? (
                                    <EditableText
                                        value={choice.text}
                                        ariaLabel={t`Edit answer choice`}
                                        onCommit={(text) =>
                                            onEditText(choice.id, text)
                                        }
                                    />
                                ) : (
                                    <Text as="span">{choice.text}</Text>
                                )}
                                {choice.correct ? (
                                    <Badge tone="success" icon={correctGlyph}>
                                        {t`Correct`}
                                    </Badge>
                                ) : null}
                            </div>
                            {onEditFeedback ? (
                                // Author path: the feedback field. When it
                                // already carries feedback it stays visible
                                // (real content — scent). An EMPTY field is the
                                // dimmed "Add feedback" target, revealed only on
                                // the choice's hover/focus (opacity, never
                                // display — it stays in the tab order + a11y
                                // tree, the "Add note" micro-pattern) so a
                                // resting choice list is not a column of standing
                                // empty fields (subtractive audit 2026-08-19).
                                <EditableText
                                    className={`bilda-render-mcq__feedback${
                                        (choice.feedback ?? "") === ""
                                            ? " bilda-render-mcq__feedback--reveal"
                                            : ""
                                    }`}
                                    value={choice.feedback ?? ""}
                                    placeholder={t`Add feedback`}
                                    ariaLabel={t`Edit answer feedback`}
                                    onCommit={(fb) =>
                                        onEditFeedback(choice.id, fb)
                                    }
                                />
                            ) : choice.feedback !== undefined ? (
                                // Read-only: render feedback only when present.
                                <Text
                                    as="p"
                                    tone="dim"
                                    size="sm"
                                    className="bilda-render-mcq__feedback"
                                >
                                    {choice.feedback}
                                </Text>
                            ) : null}
                        </div>
                        {onMoveChoice !== undefined || removable ? (
                            // Per-choice structural grip — focus-revealed reorder
                            // + remove. `toIndex` is i-1 / i+1 (index in the list
                            // with this choice removed), the move-choice contract.
                            <div className="bilda-render-mcq__choice-controls">
                                {onMoveChoice !== undefined && i > 0 ? (
                                    <IconButton
                                        variant="ghost"
                                        size="sm"
                                        label={t`Move answer up`}
                                        onClick={() =>
                                            onMoveChoice(
                                                mcqId,
                                                choice.id,
                                                i - 1,
                                            )
                                        }
                                    >
                                        <Icon name="chevron-up" size="sm" />
                                    </IconButton>
                                ) : null}
                                {onMoveChoice !== undefined &&
                                i < element.choices.length - 1 ? (
                                    <IconButton
                                        variant="ghost"
                                        size="sm"
                                        label={t`Move answer down`}
                                        onClick={() =>
                                            onMoveChoice(
                                                mcqId,
                                                choice.id,
                                                i + 1,
                                            )
                                        }
                                    >
                                        <Icon name="chevron-down" size="sm" />
                                    </IconButton>
                                ) : null}
                                {removable ? (
                                    <IconButton
                                        variant="ghost"
                                        size="sm"
                                        label={t`Remove answer`}
                                        onClick={() =>
                                            onRemoveChoice?.(mcqId, choice.id)
                                        }
                                    >
                                        <Icon name="trash" size="sm" />
                                    </IconButton>
                                ) : null}
                            </div>
                        ) : null}
                    </li>
                ))}
            </ol>
            {onAddChoice !== undefined ? (
                <div className="bilda-render-mcq__add">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onAddChoice(mcqId)}
                    >
                        <Icon name="plus" size="sm" />
                        {t`Add answer`}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

// The default registry — one entry per kind. A new Element variant widens
// ElementRenderers and breaks this object until its renderer is written.
export const defaultRenderers: ElementRenderers = {
    heading: HeadingRenderer,
    prose: ProseRenderer,
    objective: ObjectiveRenderer,
    mcq: McqRenderer,
    runs: { objective: ObjectivesRun },
};
